import type {
  EndorsementModalApi,
  EndorsementPayload,
  EventType,
  Trait,
  WorkerSuggestion,
  BrandSuggestion,
} from '../EndorsementModal/types';

/**
 * Worker-specific implementation of EndorsementModalApi.
 * Calls WorkerEndorsementController endpoints (existing Grails endpoints).
 * Uses absolute URLs (with VITE_SERVER_BASE_URL) so it works both when the
 * widget runs same-origin (GSP-embedded) and cross-origin (standalone React).
 * `credentials: 'include'` ensures the Grails session cookie is sent in both
 * cases.
 */

const SERVER_URL = import.meta.env.VITE_SERVER_BASE_URL;
const ENDORSEMENT_BASE = `${SERVER_URL}/workerEndorsement`;

async function endorsementFetch<T>(
  path: string,
  init?: RequestInit
): Promise<T | null> {
  const response = await fetch(`${ENDORSEMENT_BASE}${path}`, {
    credentials: 'include',
    ...init,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.errorMessage || `Request failed: ${path}`);
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

export interface EndorsementFormData {
  eventTypes: EventType[];
  traits: Trait[];
}

export async function fetchEndorsementFormData(): Promise<EndorsementFormData> {
  return endorsementFetch<EndorsementFormData>(
    '/endorsementFormData'
  ) as Promise<EndorsementFormData>;
}

interface WorkerSearchResponse {
  suggestions?: Array<{
    value: string;
    data: {
      id: number;
      image: string;
      email?: string;
      alreadyEndorsed: boolean;
    };
  }>;
}

function mapWorkerSuggestions(
  data: WorkerSearchResponse | null
): WorkerSuggestion[] {
  return (data?.suggestions || []).map((suggestion) => ({
    id: suggestion.data.id,
    name: suggestion.value,
    email: suggestion.data.email,
    profileImageUrl: suggestion.data.image || '',
    alreadyEndorsed: suggestion.data.alreadyEndorsed || false,
  }));
}

export function createWorkerEndorsementApi(): EndorsementModalApi {
  return {
    searchWorkers: async (query: string): Promise<WorkerSuggestion[]> => {
      const data = await endorsementFetch<WorkerSearchResponse>(
        `/searchWorkers?query=${encodeURIComponent(query)}`
      );
      return mapWorkerSuggestions(data);
    },

    searchWorkerByEmail: async (email: string): Promise<WorkerSuggestion[]> => {
      const data = await endorsementFetch<WorkerSearchResponse>(
        `/searchWorkers?email=${encodeURIComponent(email)}`
      );
      return mapWorkerSuggestions(data);
    },

    searchBrands: async (query: string): Promise<BrandSuggestion[]> => {
      const data = await endorsementFetch<{
        suggestions?: Array<{ value: string; data: { id: number } }>;
      }>(`/searchCommonBrand?query=${encodeURIComponent(query)}`);
      return (data?.suggestions || []).map((suggestion) => ({
        id: suggestion.data.id,
        name: suggestion.value,
      }));
    },

    saveEndorsement: async (data: EndorsementPayload): Promise<void> => {
      // WorkerEndorsementController.save() uses params (form-encoded), not JSON body.
      // Arrays are sent as multiple values for the same key name.
      const params = new URLSearchParams();
      if (data.workerId !== null)
        params.append('workerId', String(data.workerId));
      params.append('fullName', data.fullName);
      params.append('email', data.email);
      params.append('companyName', data.companyName);
      if (data.reviewDescription !== null)
        params.append('reviewDescription', data.reviewDescription);
      if (data.photoIsRepresentative !== null) {
        params.append(
          'photoIsRepresentative',
          String(data.photoIsRepresentative)
        );
      }
      params.append('isEndorsedManager', String(data.isEndorsedManager));
      params.append('sharedEventsNumber', data.sharedEventsNumber);
      data.sharedEventsType.forEach((id) =>
        params.append('sharedEventsType', String(id))
      );
      data.endorsedTraits.forEach((id) =>
        params.append('endorsedTraits', String(id))
      );

      await endorsementFetch('/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
    },
  };
}
